/**
 * Stand / sit / lie state machine. Behaviour only — it knows nothing about
 * meshes. It exposes two eased blend amounts (`sit`, `lie`) that the body
 * applies as poses. Downward transitions are slower than getting up, and
 * getting up from lying releases `lie` before `sit` so the dog rises through
 * a sit rather than popping straight to standing.
 */
export type Posture = 'stand' | 'sit' | 'lie';

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export class PostureController {
  state: Posture = 'stand';
  /** 0..1 eased sit amount */
  sit = 0;
  /** 0..1 eased lie amount (only meaningful when sit is high) */
  lie = 0;

  private timer = 0;
  private next = rand(6, 12);

  /** Called when the dog is asked to move: get up, and reset the idle clock. */
  wake(): void {
    this.request('stand');
  }

  /** Go to a posture now (a command), and hold it for a while before idling on. */
  request(state: Posture, holdSeconds = rand(15, 30)): void {
    this.state = state;
    this.timer = 0;
    this.next = holdSeconds;
  }

  /** How upright the dog is, 0..1. Use to gate locomotion. */
  get upright(): number {
    return 1 - Math.max(this.sit, this.lie);
  }

  update(dt: number, moving: boolean): void {
    if (moving) this.timer = 0;
    else this.timer += dt;

    if (this.state === 'stand' && !moving && this.timer > this.next) {
      this.state = 'sit';
      this.timer = 0;
      this.next = rand(8, 20);
    } else if (this.state === 'sit' && this.timer > this.next) {
      this.state = 'lie';
      this.timer = 0;
      this.next = rand(25, 60);
    } else if (this.state === 'lie' && this.timer > this.next) {
      // sits back up now and then, then lies down again — never truly static
      this.state = 'sit';
      this.timer = 0;
      this.next = rand(6, 14);
    }

    const sitT = this.state === 'stand' ? 0 : 1;
    const lieT = this.state === 'lie' ? 1 : 0;
    const sitRate = sitT > this.sit ? 3.5 : 5;
    const lieRate = lieT > this.lie ? 3 : 8;
    this.sit += (sitT - this.sit) * (1 - Math.exp(-sitRate * dt));
    this.lie += (lieT - this.lie) * (1 - Math.exp(-lieRate * dt));
  }
}
